/**
 * About — Bharat Cores credits page. Content sourced from www.bharatcores.com.
 * Reachable at /about (public route) and linked from the Login footer.
 *
 * Composition: this page is intentionally thin — each marketing band lives in
 * its own sub-component to keep the file scannable and the components easy
 * to test/reuse independently.
 */
import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ChartLineUp, GraduationCap, ArrowSquareOut, Heart } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const VSMS_PITCH_BULLETS = [
  "AES-256-GCM encryption (PII + every settings secret)",
  "Maker-checker approval for high-impact actions",
  "Multi-tenant trust isolation",
  "Per-org subscription + 2FA + brute-force lockout",
  "End-to-end audit log on every write",
  "Stripe payments + auto receipt PDF + email",
];

function MissionCard({ title, icon, children }) {
  return (
    <div className="vs-card p-5">
      <div className="flex items-center gap-2 mb-2">{icon}<span className="eyebrow">{title}</span></div>
      <div className="font-display text-lg">{children}</div>
    </div>
  );
}

function ServiceCard({ title, desc }) {
  return (
    <div className="vs-card p-5">
      <div className="eyebrow">Service</div>
      <div className="font-display text-xl mt-1.5">{title}</div>
      <p className="text-sm text-[var(--vs-text-secondary)] mt-2 leading-relaxed">{desc}</p>
    </div>
  );
}

function HeroSection() {
  return (
    <header className="border-b border-[var(--vs-border)] bg-[var(--vs-bg-subtle)]">
      <div className="max-w-5xl mx-auto px-6 py-16">
        <Link to="/login" className="text-xs font-mono uppercase tracking-widest text-[var(--vs-secondary)] hover:underline">← Back to VSMS</Link>
        <h1 className="font-display text-5xl sm:text-6xl mt-6 leading-[1.05] tracking-tight">
          Built by <span className="text-[var(--vs-secondary)]">Bharat Cores</span>.
        </h1>
        <p className="mt-4 text-lg text-[var(--vs-text-secondary)] max-w-2xl">
          Bharat Cores Solutions Pvt Ltd — a trusted Indian startup with 30+ years of combined banking & technology expertise. Specialists in IT audits, cybersecurity, and consultancy supporting banks and industries primarily across the seven sister states.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Button asChild data-testid="about-visit-site" className="bg-[var(--vs-secondary)] text-[var(--vs-bg)]">
            <a href="https://www.bharatcores.com" target="_blank" rel="noopener noreferrer">
              Visit bharatcores.com <ArrowSquareOut size={14} className="ml-1.5" />
            </a>
          </Button>
          <Button asChild variant="outline">
            <Link to="/login">Open VSMS</Link>
          </Button>
        </div>
      </div>
    </header>
  );
}

function MissionSection() {
  return (
    <section className="max-w-5xl mx-auto px-6 py-16 grid grid-cols-1 md:grid-cols-3 gap-5">
      <MissionCard title="Vision" icon={<ChartLineUp size={18} weight="duotone" className="text-[var(--vs-secondary)]" />}>
        Empowering secure IT transformations across India.
      </MissionCard>
      <MissionCard title="Mission" icon={<ShieldCheck size={18} weight="duotone" className="text-[var(--vs-secondary)]" />}>
        Delivering expert IT audits and security for Indian banks and institutions.
      </MissionCard>
      <MissionCard title="Values" icon={<Heart size={18} weight="duotone" className="text-[var(--vs-secondary)]" />}>
        Committed to integrity and excellence in IT services.
      </MissionCard>
    </section>
  );
}

function AuditServicesSection() {
  return (
    <section className="bg-[var(--vs-bg-subtle)] border-y border-[var(--vs-border)]">
      <div className="max-w-5xl mx-auto px-6 py-16">
        <div className="eyebrow mb-3">Audit Services</div>
        <h2 className="font-display text-3xl sm:text-4xl tracking-tight">Decades of expertise securing your operations.</h2>
        <p className="text-[var(--vs-text-secondary)] mt-3 max-w-2xl">
          Bharat Cores audits as per the RBI, NABARD, SEBI, and Cert-In framework and guidelines. The dedicated team ensures robust, compliant, and secure IT solutions.
        </p>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <ServiceCard title="IS Audit" desc="Comprehensive Information Systems Audits ensuring your information systems meet standards securely." />
          <ServiceCard title="AUA / KUA Audit" desc="Accurate AUA/KUA audits tailored for bank compliance with Aadhaar standards." />
          <ServiceCard title="ISO 27001 Audit" desc="Expert ISO 27001 audits to strengthen your organisation's security framework." />
        </div>
      </div>
    </section>
  );
}

function VSMSPitchSection() {
  return (
    <section className="max-w-5xl mx-auto px-6 py-16">
      <div className="eyebrow mb-3">Why VSMS</div>
      <h2 className="font-display text-3xl sm:text-4xl tracking-tight">VSMS — built by auditors, for trusts.</h2>
      <p className="text-[var(--vs-text-secondary)] mt-3 max-w-3xl">
        The Vidya Seva Management System is born from Bharat Cores' audit-first DNA. Every flow has an audit trail. Every secret is AES-256-GCM encrypted at rest. Every login is brute-force protected. Built so trust admins, hostel wardens, and donors can run NGO operations with the same rigour banks apply to their own systems.
      </p>
      <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        {VSMS_PITCH_BULLETS.map((line) => (
          <li key={line} className="flex items-start gap-2 vs-card p-3">
            <GraduationCap size={14} className="mt-0.5 text-[var(--vs-secondary)] shrink-0" weight="duotone" />
            <span>{line}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function AboutFooter() {
  return (
    <footer className="border-t border-[var(--vs-border)]">
      <div className="max-w-5xl mx-auto px-6 py-8 flex flex-wrap items-center justify-between gap-3 text-sm text-[var(--vs-text-secondary)]">
        <div>© Bharat Cores Solutions Pvt Ltd · Made in India</div>
        <a href="https://www.bharatcores.com" target="_blank" rel="noopener noreferrer" className="hover:text-[var(--vs-secondary)] font-mono text-xs">
          www.bharatcores.com <ArrowSquareOut size={11} className="inline ml-1" />
        </a>
      </div>
    </footer>
  );
}

export default function About() {
  return (
    <div className="min-h-screen bg-[var(--vs-bg)]" data-testid="about-bharat-cores">
      <HeroSection />
      <MissionSection />
      <AuditServicesSection />
      <VSMSPitchSection />
      <AboutFooter />
    </div>
  );
}
